import { Organisation } from 'types/organisation'
import * as Org from 'types/organisation'

export type RoleId = string

export type Role = {
  id: RoleId
  label: string
}

export type OtherOrganisations = { [slug: string]: { roles: RoleId[] } }

export type UserType = 'business-unit' | 'admin' | 'client' | 'absa'

export type UserRole =
  | 'Administrator'
  | 'Editor'
  | 'PortfolioManager'
  | 'BusinessUnitUser'
  | 'Client'
  | 'BankAdmin'
  | 'FLStructuring'
  | 'Trading'
  | 'Operations'
  | 'Sales'
  | 'PCT'
  | 'RiskManagement'

export type PermissionName =
  | 'product_admin.assets.view'
  | 'product_admin.portfolios.view'
  | 'product_admin.strategies.view'
  | 'product_admin.deposit.view'
  | 'product_admin.loan.view'
  | 'asset.view'
  | 'portfolio.view'
  | 'strategy.view'
  | 'backoffice.view'
  | 'organisation.bank.create'
  | 'organisation.bank.edit'
  | 'organisation.bank.view'
  | 'organisation.client.create'
  | 'organisation.client.edit'
  | 'organisation.client.view'
  | 'organisation.portfolio_manager.create'
  | 'organisation.portfolio_manager.edit'
  | 'organisation.portfolio_manager.view'
  | 'organisation.business_unit.create'
  | 'organisation.business_unit.edit'
  | 'organisation.business_unit.view'
  | 'risk.view'
  | 'role.bank.create'
  | 'role.bank.edit'
  | 'role.client.create'
  | 'role.client.edit'
  | 'role.business_unit.create'
  | 'role.business_unit.edit'
  | 'trade.create'
  | 'trade.edit'
  | 'trade.reporting.view'
  | 'trade.view'
  | 'user.bank.create'
  | 'user.bank.edit'
  | 'user.bank.view'
  | 'user.client.create'
  | 'user.client.edit'
  | 'user.client.view'
  | 'user.business_unit.create'
  | 'user.business_unit.edit'
  | 'user.business_unit.view'
  | 'settings.change_password'

export function categoryLabel(name: string): string {
  switch (name) {
    case 'organisations':
      return 'Organisation Admin'
    case 'users':
      return 'Users Admin'
    case 'roles':
      return 'Managing Roles'
    default:
      return name
  }
}

export type Permissions = {
  [K in PermissionName]: boolean
}

const defaultPermissions: Permissions = {
  'product_admin.assets.view': false,
  'product_admin.portfolios.view': false,
  'product_admin.strategies.view': false,
  'product_admin.deposit.view': false,
  'product_admin.loan.view': false,
  'asset.view': false,
  'portfolio.view': false,
  'strategy.view': false,
  'backoffice.view': false,
  'organisation.bank.create': false,
  'organisation.bank.edit': false,
  'organisation.bank.view': false,
  'organisation.client.create': false,
  'organisation.client.edit': false,
  'organisation.client.view': false,
  'organisation.portfolio_manager.create': false,
  'organisation.portfolio_manager.edit': false,
  'organisation.portfolio_manager.view': false,
  'organisation.business_unit.create': false,
  'organisation.business_unit.edit': false,
  'organisation.business_unit.view': false,
  'risk.view': false,
  'role.bank.create': false,
  'role.bank.edit': false,
  'role.client.create': false,
  'role.client.edit': false,
  'role.business_unit.create': false,
  'role.business_unit.edit': false,
  'trade.create': false,
  'trade.edit': false,
  'trade.reporting.view': false,
  'trade.view': false,
  'user.bank.create': false,
  'user.bank.edit': false,
  'user.bank.view': false,
  'user.client.create': false,
  'user.client.edit': false,
  'user.client.view': false,
  'user.business_unit.create': false,
  'user.business_unit.edit': false,
  'user.business_unit.view': false,
  'settings.change_password': true
}

export const PERMISSIONS: { [role: string]: Permissions } = {
  /* FNZ */
  Administrator: {
    ...defaultPermissions,
    'product_admin.assets.view': true,
    'product_admin.portfolios.view': true,
    'product_admin.strategies.view': true,
    'product_admin.deposit.view': true,
    'product_admin.loan.view': true,
    'asset.view': true,
    'portfolio.view': true,
    'strategy.view': true,
    'backoffice.view': true,
    'organisation.bank.view': true,
    'organisation.business_unit.view': true,
    'organisation.client.view': true,
    'organisation.portfolio_manager.view': true,
    'risk.view': true,
    'trade.create': true,
    'trade.reporting.view': true,
    'trade.view': true,
    'user.bank.view': true,
    'user.client.view': true,
    'user.business_unit.view': true
  },
  BankAdmin: {
    ...defaultPermissions,
    'product_admin.assets.view': true,
    'product_admin.portfolios.view': true,
    'product_admin.strategies.view': true,
    'product_admin.deposit.view': true,
    'product_admin.loan.view': true,
    'asset.view': true,
    'portfolio.view': true,
    'strategy.view': true,
    'backoffice.view': true,
    'organisation.bank.view': true,
    'organisation.client.view': true,
    'organisation.portfolio_manager.view': true,
    'risk.view': true,
    'trade.reporting.view': true,
    'trade.view': true,
    'user.client.view': true,
    'user.business_unit.view': true
  },
  FLStructuring: {
    ...defaultPermissions,
    'product_admin.assets.view': true,
    'product_admin.portfolios.view': true,
    'product_admin.strategies.view': true,
    'product_admin.deposit.view': true,
    'product_admin.loan.view': true,
    'asset.view': true,
    'portfolio.view': true,
    'strategy.view': true,
    'risk.view': true,
    'trade.reporting.view': true,
    'trade.view': true
  },
  Sales: {
    ...defaultPermissions,
    'product_admin.assets.view': true,
    'product_admin.portfolios.view': true,
    'product_admin.strategies.view': true,
    'product_admin.deposit.view': true,
    'product_admin.loan.view': true,
    'asset.view': true,
    'portfolio.view': true,
    'trade.reporting.view': true,
    'trade.view': true
  },
  Trading: {
    ...defaultPermissions,
    'product_admin.assets.view': true,
    'product_admin.portfolios.view': true,
    'product_admin.strategies.view': true,
    'product_admin.deposit.view': true,
    'product_admin.loan.view': true,
    'asset.view': true,
    'portfolio.view': true,
    'risk.view': true,
    'trade.reporting.view': true,
    'trade.view': true
  },
  Operations: {
    ...defaultPermissions,
    'product_admin.assets.view': true,
    'product_admin.portfolios.view': true,
    'product_admin.strategies.view': true,
    'product_admin.deposit.view': true,
    'product_admin.loan.view': true,
    'asset.view': true,
    'portfolio.view': true,
    'backoffice.view': true,
    'trade.reporting.view': true,
    'trade.view': true
  },
  RiskManagement: {
    ...defaultPermissions,
    'product_admin.assets.view': true,
    'product_admin.portfolios.view': true,
    'product_admin.strategies.view': true,
    'product_admin.deposit.view': true,
    'product_admin.loan.view': true,
    'asset.view': true,
    'portfolio.view': true,
    'risk.view': true,
    'trade.reporting.view': true,
    'trade.view': true
  },
  PCT: {
    ...defaultPermissions,
    'product_admin.assets.view': true,
    'product_admin.portfolios.view': true,
    'product_admin.strategies.view': true,
    'product_admin.deposit.view': true,
    'product_admin.loan.view': true,
    'asset.view': true,
    'risk.view': true,
    'trade.reporting.view': true,
    'trade.view': true
  },
  Client: {
    ...defaultPermissions,
    'organisation.business_unit.view': true,
    'trade.reporting.view': true,
    'trade.view': true,
    'user.client.view': true,
    'user.business_unit.view': true
  },
  BusinessUnitUser: {
    ...defaultPermissions,
    'trade.reporting.view': true,
    'trade.view': true
  },
  Editor: {
    ...defaultPermissions,
    'trade.reporting.view': true,
    'trade.view': true
  },
  PortfolioManager: {
    ...defaultPermissions,
    'product_admin.portfolios.view': true
  }
}

export class UserDisplayInfo {
  firstName: string

  lastName: string

  userName: string

  userRoles: UserRole[]

  userType: UserType
}

export class User {
  public active: boolean

  public email: string

  public firstName: string

  public id: string

  public lastName: string

  public organisation: Organisation

  public otherOrganisations: OtherOrganisations

  public roles: RoleId[]

  public userName: string

  private permissions: Permissions

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(user: any = {}) {
    this.active = true
    this.email = user.email
    this.firstName = user.firstName
    this.id = user.email
    this.lastName = user.lastName
    this.organisation = user.organisation
    this.otherOrganisations = {}
    this.roles = user.roles as RoleId[]
    // Merge permissions for each role the user has.
    this.permissions =
      user.roles &&
      user.roles.reduce(
        (perm, role) => {
          const rolePermissions = PERMISSIONS[role] || {}
          for (const key in rolePermissions) {
            if (rolePermissions[key]) {
              perm[key] = true
            }
          }
          return perm
        },
        { ...defaultPermissions }
      )
    this.userName = user.userName
  }

  public fullNameAlt(): string {
    return `${this.firstName} ${this.lastName}`
  }

  public fullName(): string {
    return `${this.firstName} ${this.lastName}`
  }

  public can(perm: PermissionName): boolean {
    return this.permissions[perm] || false
  }

  public canForOrgs(orgType: Org.Type, perm: 'create' | 'edit' | 'view'): boolean {
    switch (orgType) {
      case Org.Type.Bank:
        switch (perm) {
          case 'create':
            return this.can('organisation.bank.create')
          case 'edit':
            return this.can('organisation.bank.edit')
          case 'view':
            return this.can('organisation.bank.view')
          default:
            return false
        }
      case Org.Type.Client:
        switch (perm) {
          case 'create':
            return this.can('organisation.client.create')
          case 'edit':
            return this.can('organisation.client.edit')
          case 'view':
            return this.can('organisation.client.view')
          default:
            return false
        }
      case Org.Type.PortfolioManager:
        switch (perm) {
          case 'create':
            return this.can('organisation.portfolio_manager.create')
          case 'edit':
            return this.can('organisation.portfolio_manager.edit')
          case 'view':
            return this.can('organisation.portfolio_manager.view')
          default:
            return false
        }
      case Org.Type.BusinessUnit:
        switch (perm) {
          case 'create':
            return this.can('organisation.business_unit.create')
          case 'edit':
            return this.can('organisation.business_unit.edit')
          case 'view':
            return this.can('organisation.business_unit.view')
          default:
            return false
        }
    }
  }

  public canForUsers(orgType: Org.Type, perm: 'create' | 'edit' | 'view'): boolean {
    switch (orgType) {
      case Org.Type.Bank:
        switch (perm) {
          case 'create':
            return this.can('user.bank.create')
          case 'edit':
            return this.can('user.bank.edit')
          case 'view':
            return this.can('user.bank.view')
          default:
            return false
        }
      case Org.Type.Client:
        switch (perm) {
          case 'create':
            return this.can('user.client.create')
          case 'edit':
            return this.can('user.client.edit')
          case 'view':
            return this.can('user.client.view')
          default:
            return false
        }
      case Org.Type.BusinessUnit:
        switch (perm) {
          case 'create':
            return this.can('user.business_unit.create')
          case 'edit':
            return this.can('user.business_unit.edit')
          case 'view':
            return this.can('user.business_unit.view')
          default:
            return false
        }
    }
  }
}

export const BANK_ROLES: Role[] = [
  {
    id: 'admin',
    label: 'Admin'
  },
  {
    id: 'structuring',
    label: 'Structuring'
  },
  {
    id: 'sales',
    label: 'Sales'
  },
  {
    id: 'trading',
    label: 'Trading'
  },
  {
    id: 'back_office',
    label: 'Back Office'
  },
  {
    id: 'business_support',
    label: 'Business Support'
  }
]

export const BUSINESS_UNIT_ROLES: Role[] = [
  {
    id: 'view_only',
    label: 'View Only'
  },
  {
    id: 'editor',
    label: 'Editor'
  }
]

export const CLIENT_ROLES: Role[] = [
  {
    id: 'admin',
    label: 'Admin'
  }
]

export const DISCRETIONARY_FUND_MANAGER_ROLES: Role[] = [
  {
    id: 'portfolio_manager',
    label: 'Portfolio Manager'
  }
]
